/* eslint-disable no-param-reassign */
import { Box, Button, CircularProgress, Grid, Icon } from '@material-ui/core';
import { NumericMetricInfo, TagMetricInfo, TimeSeriesAggregation, TimeSeriesData, TimeSeriesRawData, TimeSeriesResolution }
    from '@moderepo/mode-apis';
import { DEFAULT_TSDB_SMART_MODULE_ID, GlobalTimeSeriesInfo, capitalizeAll, useMergeCachedMetricDataToMultiMetricDataPoints,
    resolutionToMilliseconds, TimeSeriesType, Time, DataPoint, getMidnightDate, GenericMessageBox } from '@moderepo/mode-app-components';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import produce, { Draft } from 'immer';
import { userAppDataContext, fetchTimeSeriesData, selectTimeSeriesData, fetchTimeSeriesRawData, selectTimeSeriesRawData }
    from '@moderepo/mode-data-state-user-app';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { CachedMetricData, ChartSize, MAX_DATA_FIELD_POSTFIX, MIN_DATA_FIELD_POSTFIX, ExtTimeSeriesAggregation } from './tsUtils';
import { DownloadTSDataCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { MetricsDataDisplayContainer, MetricsDataDisplayContainerProps } from './MetricsDataDisplayContainer';



interface SeriesIdInfo {
    readonly seriesId: string;
    readonly name?: string | undefined,
    readonly displayName?: string | undefined;
}


/**
 * An interface for keeping track of succeeded fetch data API calls
 */
interface FetchDataResult {
    readonly seriesId: string;
    readonly aggregation: TimeSeriesAggregation | undefined;
}


/**
 * We need this in a few places therefore will create an array of avg/min/max aggregation and reuse it.
 */
const AVG_MIN_MAX_AGGREGATIONS = [
    TimeSeriesAggregation.AVG,
    TimeSeriesAggregation.MIN,
    TimeSeriesAggregation.MAX,
];


export interface SimpleSeriesMetricsDataProviderProps {
    readonly projectId: number;

    // The container that uses this component must provide either deviceId or homeId. We actually need homeId but if homeId is not provided,
    // we can use deviceId to fetch device data and get the homeId from the device data.
    readonly deviceId?: number;
    readonly homeId?: number;

    // This is a Generic component so it doesn't know how time seriesIds are formatted. It can't parse the seriesId to get the measurement's
    // name e.g. from "0127:ea52b9cd427c-ambient:0" to "Ambient". It is up to the user of this component to provide the SeriesIdInfo for each
    // seriesId. The seriesIdInfo can contain the actual valueName and displayName for each seriesId.
    readonly timeSeriesIds: readonly SeriesIdInfo[];

    readonly timeSeriesDisplayName?: string | undefined;
    readonly tsdbSmartModuleId?: string;

    readonly viewTypeSettings?: {
        chart?: {
            readonly enabled: boolean;
            readonly excludedMetricsNames?: readonly string[];
        },
        tabular?: {
            readonly enabled: boolean;
            readonly excludedMetricsNames?: readonly string[];
        }
    };

    readonly viewOptions?: {
        readonly combineSeries?: boolean | undefined;                    // whether or not to combine multiple series into 1 chart
        readonly chartSize?: ChartSize | undefined;
        readonly showChartView?: boolean | undefined;
        readonly showTabularView?: boolean | undefined;
        readonly showChartBullets?: boolean | undefined;
        readonly aggregation?: ExtTimeSeriesAggregation | undefined;
    };

    /**
     * This Container has an action that it needs to be displayed as a page CTA. In other to do this, the parent container must
     * implement the handler function setPageMainAction and take the provided 'action' and display it on the page. The parent container ODES NOT
     * need to do anything with this action other than displaying it. This container will take care of the UI of the action and will handle all the
     * events.
     */
    readonly setPageMainAction: (action: JSX.Element)=> void;
}



/**
 * This is a generic container that is used for displaying metric data from SIMPLE time series.
 */
export const SimpleSeriesMetricsDataProvider = (props: SimpleSeriesMetricsDataProviderProps) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const device = props.deviceId ? selectProjectDeviceById(dataState, props.deviceId) : undefined;
    const { trans } = useRitzTranslation(project);


    // We need homeId to fetch time series data. The homeId can be either in the route params if the user is in the route path that has
    // homeId. If not, we need to get the homeId from the device
    const timeSeriesHomeId = useMemo(() => {
        if (props.homeId) {
            return props.homeId;
        }
        if (device && device.homeId) {
            return device.homeId;
        }
        return undefined;
    }, [device, props.homeId]);


    // We will keep the last requested data in cache. Before we request for new data, we will check this cache first to see if we have data for
    // the request params. If yes, we return data from the cache. If not, we will fetch new data.
    const [cachedMetricsData, setCachedMetricsData] = useState<CachedMetricData>({
        start: undefined, end: undefined, limit: undefined, aggregation: undefined, numericMetrics: {},
    });



    // This state is used for remembering the params we used for fetching data so that we can use the same params to select the data from data state
    const fetchDataParams = useRef<{
        readonly start: number;
        readonly end?: number | undefined;
        readonly limit?: number | undefined;
        readonly aggregation?: ExtTimeSeriesAggregation | undefined;
        readonly numericMetrics: readonly NumericMetricInfo[];
    } | undefined>();

    // For time series we need to make multiple API call to fetch data 1 for each value name.
    // We need to wait for all the data to be fetched before we can try to select them from the data state.
    // Therefore, this state is to keep track of when the data is loaded and which time series was fetched successfully.
    const [succeedFetchDataResults, setSucceedFetchDataResults] = useState<readonly FetchDataResult[] | undefined>();


    const [globalTimeSeriesInfo, setGlobalTimeSeriesInfo] = useState<GlobalTimeSeriesInfo<NumericMetricInfo[]> | undefined>();

    /**
     * Wait for timeSeriesInfo and timeSeriesRange data to be loaded and create the globalTimeSeriesInfo
     */
    useEffect(() => {
        if (timeSeriesHomeId && !globalTimeSeriesInfo) {

            if (project) {
                // Now we have all the information we need to create a GlobalTimeSeriesInfo object
                setGlobalTimeSeriesInfo({
                    dateRange: {
                        start: getMidnightDate(moment(project.creationTime).toDate()).valueOf(),
                        end  : getMidnightDate(moment(Date.now()).add(1, 'day').toDate()).valueOf(),
                    },
                    actualDateRange: {
                        start: moment(project.creationTime).valueOf(),
                        end  : moment(Date.now()).valueOf(),
                    },
                    numericMetrics: props.timeSeriesIds.map((seriesIdInfo: SeriesIdInfo) => {
                        return {
                            name       : seriesIdInfo.name || seriesIdInfo.seriesId,
                            displayName: seriesIdInfo.displayName || seriesIdInfo.name || seriesIdInfo.seriesId,
                        };
                    }),
                });
            }
        }
    }, [appDataState, timeSeriesHomeId, dataState, globalTimeSeriesInfo, project, props.timeSeriesIds, props.tsdbSmartModuleId,
        props.timeSeriesDisplayName]);



    /**
     * This useEffect is used for waiting for time series data to be fetched and then cache them
     */
    useEffect(() => {
        if (timeSeriesHomeId && fetchDataParams.current && succeedFetchDataResults) {
            const lastFetchDataParams = fetchDataParams.current;
            const successFetchResults = succeedFetchDataResults;

            fetchDataParams.current = undefined;
            setSucceedFetchDataResults(undefined);

            // We need to do this check in many places so create a variable for this for convenience
            const isAvgMinMaxAggregation = lastFetchDataParams.aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX;

            // Select the time series data from the data state using the same fetchDataParams we used when we fetch data
            const allTimeSeriesData = successFetchResults.map((singleFetchResult: FetchDataResult) => {
                return lastFetchDataParams.aggregation === undefined
                    ? selectTimeSeriesRawData(
                        appDataState, timeSeriesHomeId,
                        props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        singleFetchResult.seriesId,
                        moment(lastFetchDataParams.start).toISOString(),
                        lastFetchDataParams.limit as number,
                    )
                    : selectTimeSeriesData(
                        appDataState, timeSeriesHomeId,
                        props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        singleFetchResult.seriesId,
                        moment(lastFetchDataParams.start).toISOString(),
                        moment(lastFetchDataParams.end).toISOString(),
                        singleFetchResult.aggregation,
                    );
            });

            // Filter out undefined data from failed fetch data calls
            const nonEmptyTimeSeriesData = allTimeSeriesData.filter(
                (data: TimeSeriesData | TimeSeriesRawData | undefined) => {
                    return data !== undefined;
                },
            ) as (TimeSeriesRawData | TimeSeriesData)[];


            /**
             * Initialize newData object for all metrics and initialize them with empty arrays. This will create this object for example
             *      {
             *          temperature: [],
             *          pressure: [],
             *          humidity: [],
             *          ...
             *      }
             *
             * For AVG_MIN_MAX aggregation, we will add additional metrics for min/max
             *      {
             *          temperature: [],
             *          temperature_min: [],
             *          temperature_max: [],
             *          pressure: [],
             *          pressure_min: [],
             *          pressure_max: [],
             *          humidity: [],
             *          humidity_min: [],
             *          humidity_max: [],
             *          ...
             *      }
             */
            const newDataInit = lastFetchDataParams.numericMetrics.reduce((
                result: {[sensorValueName: string]: DataPoint<number>[]}, numericMetric: NumericMetricInfo,
            ): {[sensorValueName: string]: DataPoint<number>[]} => {
                result[numericMetric.name] = [];
                if (isAvgMinMaxAggregation) {
                    result[`${numericMetric.name}${MIN_DATA_FIELD_POSTFIX}`] = [];
                    result[`${numericMetric.name}${MAX_DATA_FIELD_POSTFIX}`] = [];
                }
                return result;
            }, {});


            /**
             * Take the data from nonEmptyTimeSeriesData and add them into the newDataInit
             *      {
             *          temperature: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
             *          pressure: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
             *          humidity: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
             *          ...
             *      }
             *
             * For AVG_MIN_MAX aggregation, we will also include data for the min/max metrics
             *      {
             *          temperature: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
             *          temperature_min: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
             *          temperature_max: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
             *          pressure: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
             *          pressure_min: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
             *          pressure_max: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
             *          humidity: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
             *          humidity_min: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
             *          humidity_max: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
             *          ...
             *      }
            */
            const newData = nonEmptyTimeSeriesData.reduce((result: typeof newDataInit, seriesData: TimeSeriesData | TimeSeriesRawData): any => {
                // Look for the seriesIdInfo associated with the seriesData's ID
                const seriesIdInfo = props.timeSeriesIds.find((sidInfo: SeriesIdInfo) => {
                    return seriesData.seriesId === sidInfo.seriesId;
                });

                // Get the name of the metrics. Note that metric name is NOT always seriesIdInfo.name. If we are using an aggregation which
                // only have 1 set of data then it is seriesIdInfo.name. However, if we are using AVG_MIN_MAX aggregation, the name of the
                // metrics will depends on the data we fetched. We fetch 3 sets of data, AGV, MIN, and MAX. If the data's aggregation is AGV then
                // the name will be the same since it is the primary data. But if the fetched data's aggregation is MIN then we will append
                // '_min' to the metric's name and '_max' for data with MAX aggregation.
                const valueName = (() => {
                    const metricName = seriesIdInfo?.name || seriesData.seriesId;
                    if (isAvgMinMaxAggregation && (seriesData as TimeSeriesData).aggregation === TimeSeriesAggregation.MIN) {
                        return `${metricName}${MIN_DATA_FIELD_POSTFIX}`;
                    }
                    if (isAvgMinMaxAggregation && (seriesData as TimeSeriesData).aggregation === TimeSeriesAggregation.MAX) {
                        return `${metricName}${MAX_DATA_FIELD_POSTFIX}`;
                    }
                    return metricName;
                })();

                result[valueName] = seriesData.data.map(([date, value]): DataPoint<number> => {
                    return {
                        dateString: date, dateValue: Date.parse(date), value,
                    };
                });

                return result;
            }, newDataInit);



            // Find the SMALLEST data resolution from all the seriesData and use that as the data resolution for all the data.
            const dataResolution = lastFetchDataParams.aggregation !== undefined ? nonEmptyTimeSeriesData.reduce((
                result: TimeSeriesResolution | undefined, seriesData: TimeSeriesData | TimeSeriesRawData,
            ): TimeSeriesResolution | undefined => {
                const res = (seriesData as TimeSeriesData).resolution;
                if (!result) {
                    // If this is the FIRST data resolution we found, use this resolution
                    return res as TimeSeriesResolution;
                }

                // If we already found a data resolution AND if the new seriesData also has a resolution, compare them and use the smaller one
                if (result && res) {
                    if (resolutionToMilliseconds(result as string) > resolutionToMilliseconds(res)) {
                        return res as TimeSeriesResolution;
                    }
                }

                return result;
            }, undefined) : undefined;



            // now cache the data.
            // NOTE: if only sensors array changed then we can keep the cached time series data.
            // If start or end date or aggregation changed, we need to throw away all the cached time series data.
            setCachedMetricsData(produce(cachedMetricsData, (cache: Draft<CachedMetricData>) => {
                cache.dataResolution = dataResolution;
                cache.dataResolutionValue = dataResolution ? resolutionToMilliseconds(dataResolution as string) : undefined;

                if (
                    lastFetchDataParams.aggregation !== cache.aggregation
                    || lastFetchDataParams.start !== cache.start
                    || lastFetchDataParams.end !== cache.end
                    || lastFetchDataParams.limit !== cache.limit
                ) {
                    // If date changed or aggregation changed, replace all the cached data with new data since to old data no longer valid
                    cache.aggregation = lastFetchDataParams.aggregation;
                    cache.start = lastFetchDataParams.start;
                    cache.end = lastFetchDataParams.end;
                    cache.limit = lastFetchDataParams.limit;
                    cache.numericMetrics = newData;
                } else {
                    // If date has not changed, continue using the previous old data and add new data to the cached data by
                    // copying the current data from cache and override some of the data with new data
                    cache.numericMetrics = {
                        ...cache.numericMetrics,
                        ...newData,
                    };
                }
            }));
        }
    }, [appDataState, cachedMetricsData, timeSeriesHomeId, dataState, props.timeSeriesIds, props.tsdbSmartModuleId, device, succeedFetchDataResults]);



    // Whenever cachedTimeSeriesData is changed, we will localize it right away so that it can be displayed in the chart/table.
    // This data set can be large so we don't want to do this on every render
    const multiNumericMetricDataPoints = useMergeCachedMetricDataToMultiMetricDataPoints(cachedMetricsData);


    /**
     * This useEffect is used for loading device data only if homeId is not provided. We need homeId to fetch time series
     * so if props.homeId is not provided, we will fetch device data and get the homeId from the device data instead
     */
    useEffect(() => {
        if (!props.homeId && props.deviceId && !device) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [props.projectId, dataDispatch, device, props.deviceId, props.homeId]);



    /**
     * Handle fetch time series data when NumericDataViewContainer needs to fetch more data
     */
    const onFetchTimeSeriesDataHandler: MetricsDataDisplayContainerProps['fetchTimeSeriesDataHandler'] = useCallback(async (
        selectedNumericMetrics: readonly NumericMetricInfo[], selectedTagMetrics: readonly TagMetricInfo[],
        aggregation: ExtTimeSeriesAggregation | undefined,
        start: number,
        end: number | undefined,        // undefined if aggregation type IS undefined
        limit: number | undefined,      // undefined if aggregation type IS NOT undefined
    ) => {
        // first, check to see which time series we need to load data for. We don't need to load time series data for all selected metrics
        // because some time all the user does toggle the metric visibility.
        const filteredNumericMetrics = selectedNumericMetrics.filter((numericMetric: NumericMetricInfo) => {
            // Return true if we have not cache ANY time series data or we don't have cached data for this specific metric
            // or we have cached data for this metric but the cached start or end date or aggregation is different.
            return !cachedMetricsData || !cachedMetricsData.numericMetrics
                    || !cachedMetricsData.numericMetrics[numericMetric.name]
                    || cachedMetricsData.aggregation !== aggregation
                    || (cachedMetricsData.start === undefined || cachedMetricsData.start.valueOf() !== start.valueOf())
                    || (limit && (cachedMetricsData.limit === undefined || cachedMetricsData.limit.valueOf() !== limit.valueOf()))
                    || (end !== undefined && (cachedMetricsData.end === undefined || cachedMetricsData.end.valueOf() !== end.valueOf()));
        });


        // now request data for the metrics that need to fetch data for
        if (filteredNumericMetrics.length > 0 && timeSeriesHomeId) {

            // We need to do this check in many places so create a variable for this for convenience
            const isAvgMinMaxAggregation = aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX;

            // Build a list actions to fetch time series data, one for each series
            const fetchActions = filteredNumericMetrics.map((metric: NumericMetricInfo) => {

                // Look for the seriesIdInfo associated with the metric's valueName
                const seriesIdInfo = props.timeSeriesIds.find((sidInfo: SeriesIdInfo) => {
                    return (sidInfo.name || sidInfo.seriesId) === metric.name;
                });

                // Create an action to fetch data
                if (aggregation === undefined) {
                    // If the aggregation, fetch RAW data
                    return appDataDispatch(fetchTimeSeriesRawData(
                        timeSeriesHomeId,
                        props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        seriesIdInfo?.seriesId || metric.name,
                        moment(start).toISOString(),
                        limit as number,
                    ), false, true);
                }

                // If isMinMaxAvgAggregation, create 3 actions to fetch min/max/avg aggregated data
                if (isAvgMinMaxAggregation) {
                    const actions: Promise<void>[] = [];
                    for (let i = 0; i < AVG_MIN_MAX_AGGREGATIONS.length; i += 1) {
                        actions.push(appDataDispatch(fetchTimeSeriesData(
                            timeSeriesHomeId,
                            props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                            seriesIdInfo?.seriesId || metric.name,
                            moment(start).toISOString(),
                            moment(end as number).toISOString(),
                            AVG_MIN_MAX_AGGREGATIONS[i],
                        ), false, true));
                    }
                    return actions;
                }

                // Else create only 1 action to fetch data
                return appDataDispatch(fetchTimeSeriesData(
                    timeSeriesHomeId,
                    props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                    seriesIdInfo?.seriesId || metric.name,
                    moment(start).toISOString(),
                    moment(end as number).toISOString(),
                    (aggregation as unknown) as TimeSeriesAggregation,
                ), false, true);
            }).flat();


            // After the data is loaded, we will need to select it from the data state. To select it from the data state, we will need to specify
            // the params we use when we fetch the data. Therefore, we need to save these params so that we can use them to select data later.
            fetchDataParams.current = {
                start,
                end,
                limit,
                aggregation,
                numericMetrics: filteredNumericMetrics,
            };

            // Wait for all the fetch data action to be done and compile a list of fetch data call that was fetched successfully.
            const succeedFetchResults = await Promise.allSettled(fetchActions)
                .then((allResults) => {

                    // Loop through the list of 'allResults' and create a FetchResult object for each succeed fetch calls. If a call failed
                    // return undefined. By the end of this loop, 'allFetchResults', should contain an array of 'FetchResult' objects and
                    // maybe some 'undefined'
                    const allFetchResults = allResults.map((
                        singleResult, resultIndex: number,
                    ): FetchDataResult | undefined => {
                        if (singleResult.status === 'fulfilled') {

                            // Because we make 3 API calls for some aggregate type, AVG_MIN_MAX, the index of the result will not be the same
                            // as the index of the list of metrics. For example: if there are 2 metrics then there will be 2 x 3 = 6 api calls
                            // therefore there will be 6 results. So to get the name of the metric for result at resultIndex, we have to device the
                            // resultIndex by 3. The result at index 0, 1, 2 will be for the metric at index 0. The result at index 3, 4, 5 will
                            // be for metric at index 1, and so on.
                            const metricIndex = isAvgMinMaxAggregation ? Math.floor(resultIndex / 3) : resultIndex;
                            const { name } = filteredNumericMetrics[metricIndex];

                            // Look for the seriesIdInfo associated with the metric's valueName
                            const seriesIdInfo = props.timeSeriesIds.find((sidInfo: SeriesIdInfo) => {
                                return (sidInfo.name || sidInfo.seriesId) === name;
                            });

                            if (seriesIdInfo) {
                                // Create and return a FetchResult object
                                if (isAvgMinMaxAggregation) {
                                    // if isMinMaxAvgAggregation then the aggregation will depend on the resultIndex. Because we make API call
                                    // in this order AVG, MIN, MAX then the results will also be in this order
                                    // result at index 0, 3, 6, etc... will be for the API calls that fetch AGV data
                                    // result at index 1, 4, 7, etc... will be for the API calls that fetch MIN data
                                    // result at index 2, 5, 8, etc... will be for the API calls that fetch MAX data
                                    return {
                                        seriesId   : seriesIdInfo.seriesId,
                                        aggregation: AVG_MIN_MAX_AGGREGATIONS[resultIndex % 3],
                                    };
                                }

                                // If not isMinMaxAvgAggregation then the aggregation is the same as the provided aggregation
                                return {
                                    seriesId   : seriesIdInfo.seriesId,
                                    aggregation: aggregation ? (aggregation as unknown) as TimeSeriesAggregation : aggregation,
                                };
                            }
                        }
                        return undefined;
                    });

                    // Filter out 'undefined' results because those are unsuccess fetch alls
                    return allFetchResults.reduce((result: FetchDataResult[], singleFetchResult: FetchDataResult | undefined) => {
                        if (singleFetchResult) {
                            return [...result, singleFetchResult];
                        }
                        return result;
                    }, []);
                });

            setSucceedFetchDataResults(succeedFetchResults);
        }
    }, [timeSeriesHomeId, cachedMetricsData, props.timeSeriesIds, props.tsdbSmartModuleId, appDataDispatch]);


    /**
     * On Download Data clicked handler. Show the Download Data Request panel and pass it the time series the user is trying to download
     * and set the default selection data range to 1 Month
     */
    const downloadDataHandler = useCallback(() => {
        if (globalTimeSeriesInfo && globalTimeSeriesInfo.numericMetrics && timeSeriesHomeId) {
            const options: DownloadTSDataCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.DOWNLOAD_TS_DATA_REQUEST,
                compProps: {
                    data: {
                        projectId        : props.projectId,
                        homeId           : timeSeriesHomeId,
                        tsdbSmartModuleId: props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        dataInfo         : globalTimeSeriesInfo.numericMetrics.map((metric: NumericMetricInfo) => {
                            return {
                                type    : TimeSeriesType.SIMPLE,
                                id      : metric.name,
                                name    : metric.displayName || metric.name,
                                selected: true,
                            };
                        }),
                        startDate: Math.max(globalTimeSeriesInfo.actualDateRange.start, globalTimeSeriesInfo.actualDateRange.end - Time.MONTH_IN_MS),
                        endDate  : globalTimeSeriesInfo.actualDateRange.end - 1,
                        minDate  : globalTimeSeriesInfo.actualDateRange.start,
                        maxDate  : globalTimeSeriesInfo.actualDateRange.end,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [globalTimeSeriesInfo, timeSeriesHomeId, props.projectId, props.tsdbSmartModuleId, uiDispatch]);


    /**
     * Create a Download Button which will be passed to the parent container for display
     */
    const downloadButton = useMemo(() => {
        return (
            <Button
                color="primary"
                variant="contained"
                startIcon={<Icon>get_app</Icon>}
                disabled={!(globalTimeSeriesInfo && timeSeriesHomeId)}
                onClick={() => {
                    downloadDataHandler();
                }}
            >{capitalizeAll(trans('label.download_time_series_data'))}
            </Button>
        );
    }, [timeSeriesHomeId, downloadDataHandler, globalTimeSeriesInfo, trans]);


    /**
     * on component init, call the parent's setExternalComp handler function and pass it the download button to display
     */
    useEffect(() => {
        props.setPageMainAction(downloadButton);
    }, [downloadButton, props, props.setPageMainAction]);


    const isFetchingData = Boolean(
        !project
        || !timeSeriesHomeId
        || !globalTimeSeriesInfo,
    );


    const hasNumericMetricData = globalTimeSeriesInfo && globalTimeSeriesInfo.numericMetrics && globalTimeSeriesInfo.numericMetrics.length > 0
        && multiNumericMetricDataPoints && multiNumericMetricDataPoints.numericMetrics !== undefined;


    return (
        <>
            {!isFetchingData && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {globalTimeSeriesInfo && hasNumericMetricData && (
                            <MetricsDataDisplayContainer
                                projectId={props.projectId}
                                absoluteStartDate={globalTimeSeriesInfo.actualDateRange.start}
                                absoluteEndDate={globalTimeSeriesInfo.actualDateRange.end}
                                numericMetrics={globalTimeSeriesInfo.numericMetrics || []}
                                tagMetrics={[]}
                                multiSeriesNumericDataPoints={multiNumericMetricDataPoints?.numericMetrics || []}
                                multiSeriesTagDataPoints={[]}
                                fetchTimeSeriesDataHandler={onFetchTimeSeriesDataHandler}
                                viewOptions={props.viewOptions}
                                dataResolution={cachedMetricsData.dataResolution}
                                viewTypeSettings={props.viewTypeSettings}
                            />
                        )}

                        {!hasNumericMetricData && (
                            /* Show empty message if there is no time series */
                            <Box marginTop={3}>
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                    })}
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}


            {isFetchingData && (
                <CircularProgress />
            )}
        </>
    );
};
